import type { PostAIConfigRequestBody } from '@gonfalon/openapi/operations/postAIConfig';

import { modelProviders } from '#components/AiConfigs/AiConfigModelConfiguration/modelSchemas/aiConfigModelService';
import { asUnknownRecord } from '#components/AiConfigs/AiConfigVariations/parsers';
import { EDITABLE_EVAL_KEY_PREFIX } from '#components/AiConfigs/constants';

import type { JudgeTemplateFormData } from './form/schema';
import { DEFAULT_JUDGE_PROPERTIES } from './templates/data/default';
import { providerToDefaultModel } from './templates/factory/constants';
import { type JudgeTemplateKey, judgeTemplateRegistry } from './templates/judgeTemplateRegistry';
import type { JudgeTemplate } from './types';

export function isModelKeyConsistentWithProvider(
  modelConfigKey: string | undefined,
  provider: string,
): modelConfigKey is string {
  if (!modelConfigKey) {
    return false;
  }
  if (modelConfigKey.startsWith(`${provider}.`)) {
    return true;
  }
  return !modelProviders.some((p) => modelConfigKey.startsWith(`${p}.`));
}

function getDefaultJudgePayload(
  provider: JudgeTemplateFormData['provider'],
  modelConfigKey?: string,
): Omit<PostAIConfigRequestBody, 'key' | 'name'> {
  const model = providerToDefaultModel[provider];
  const effectiveModelName = isModelKeyConsistentWithProvider(modelConfigKey, provider)
    ? modelConfigKey
    : model.modelName;

  return {
    mode: 'judge',
    description: DEFAULT_JUDGE_PROPERTIES.description,
    isInverted: DEFAULT_JUDGE_PROPERTIES.isInverted,
    tags: DEFAULT_JUDGE_PROPERTIES.tags,
    defaultVariation: {
      key: 'default',
      name: 'Default',
      model: asUnknownRecord({
        modelName: effectiveModelName,
        parameters: model.parameters,
        custom: model.custom,
      }),
      modelConfigKey: effectiveModelName,
      messages: DEFAULT_JUDGE_PROPERTIES.messages,
    },
  } as Omit<PostAIConfigRequestBody, 'key' | 'name'>;
}

export function getTemplateJudgePayload(template: JudgeTemplate): Omit<PostAIConfigRequestBody, 'key' | 'name'> {
  return {
    mode: 'judge',
    description: template.description,
    isInverted: template.isInverted,
    tags: ['ai', 'judge'],
    defaultVariation: {
      key: 'default',
      name: 'Default',
      model: asUnknownRecord({
        modelName: template.model.modelName,
        parameters: template.model.parameters,
        custom: template.model.custom,
      }),
      modelConfigKey: template.model.modelName,
      messages: template.messages,
      tools: template.tools?.map((tool) => ({
        key: tool.key,
        version: tool.version,
      })),
    },
  } as Omit<PostAIConfigRequestBody, 'key' | 'name'>;
}

export function parseCreateJudgeFormPayload(
  form: JudgeTemplateFormData,
  { applyModelConfigKey = true }: { applyModelConfigKey?: boolean } = {},
): PostAIConfigRequestBody {
  const { templateKey, provider } = form;
  const modelConfigKey = applyModelConfigKey ? form.modelConfigKey : undefined;
  const userInput = {
    name: form.name,
    key: form.key,
    evaluationMetricKey: form.evaluationMetricKey,
    maintainerId: form.maintainerMemberId,
    maintainerTeamKey: form.maintainerTeamKey,
  };

  // If no template is selected, use fallback properties
  if (!templateKey) {
    return {
      ...userInput,
      ...getDefaultJudgePayload(provider, modelConfigKey),
    };
  }

  // Use selected template with model override from the picker
  // getTemplateByProvider always returns a template since all templates support all providers
  const template = judgeTemplateRegistry[templateKey].getTemplateByProvider(provider);
  const payload = getTemplateJudgePayload(template);

  if (isModelKeyConsistentWithProvider(modelConfigKey, provider) && payload.defaultVariation) {
    payload.defaultVariation.model = asUnknownRecord({
      modelName: modelConfigKey,
      parameters: template.model.parameters,
      custom: template.model.custom,
    });
    payload.defaultVariation.modelConfigKey = modelConfigKey;
  }

  return {
    ...userInput,
    ...payload,
  };
}

export function asTemplateKeyOrNull(templateKey: string): JudgeTemplateKey | null {
  const template = judgeTemplateRegistry[templateKey as JudgeTemplateKey];
  if (!template) {
    return null;
  }
  return template.key;
}

export function parseEvalKeyToSuffix(key: string): string {
  if (!key.startsWith(EDITABLE_EVAL_KEY_PREFIX)) {
    // If it doesn't start with the prefix, return as-is (for backwards compatibility)
    return key;
  }
  return key.slice(EDITABLE_EVAL_KEY_PREFIX.length);
}

export function parseSuffixToEvalKey(suffix: string): string {
  return `${EDITABLE_EVAL_KEY_PREFIX}${suffix}`;
}
